import { AgentRole } from "@src/appV2/Agents";
import { environmentConfig } from "@src/appV2/environment";
import { SEARCH_MODE } from "@src/constants";
import { logAndThrowApiError, logApiFailureEvent } from "@src/lib/analytics";
import { Agent, Shift } from "@src/lib/interface";
import { DateRange } from "@src/lib/utils";
import {
  WorkerServiceEndpoints,
  getWorkerServiceMappedURL,
} from "@src/utils/worker-service/worker-service.utils";
import { fetchYourShifts } from "@store/shift/api";
import { reduce } from "lodash";
import moment from "moment-timezone";
import request from "superagent";

import { RequestDetailedRatings, ResponseDetailedRatings, ShiftDateMap } from "./model";
import { getAuthHeader } from "../superagent";

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const reduceToDate = (acc: ShiftDateMap, shift: Shift): ShiftDateMap => {
  const day = moment(shift.start).format("YYYY-MM-DD");

  return {
    ...acc,
    [day]: [...(acc[day] || []), shift],
  };
};
/**
 * @todo After CH-17726, Switch to using {@link fetchYourShifts} with `groupByDate=true` and remove `.then` part.
 * @link https://clipboardhealth.atlassian.net/browse/CH-17726
 */
export const fetchYourShiftsByDateAggregated = async (
  dateFilter: DateRange
): Promise<ShiftDateMap> => {
  return fetchYourShifts({ dateFilter }).then((agentShifts) => {
    return reduce(agentShifts, reduceToDate, {});
  });
};

export const markShiftInterest = async (
  shiftId: string,
  activityLog: any
): Promise<Shift | any> => {
  return await request
    .post(`${environmentConfig.REACT_APP_BASE_API_URL}/shift/interested`)
    .set(await getAuthHeader())
    .send({ shift: shiftId, ...activityLog, isNative: true, docVer: "v2" })
    .then(({ body }) => body)
    .catch((error) => {
      logApiFailureEvent(error);
      const { response } = error;
      return { error: response.body.error || response.error.text };
    });
};

interface ClaimAgentShiftPayload {
  shiftId: string;
  offerId?: string;
  hasLastMinuteGrabCheck?: boolean;
  longitude?: number;
  latitude?: number;
  searchMode?: SEARCH_MODE;
  availableShifts?: string[];
  filterDistance?: number;
  filterDays?: number;
}

export interface AgentShiftResponse extends Shift {
  _id: string;
  agentId: string;
  agentReq: AgentRole;
  assignedBy: string;
  charge: number;
  pay: number;
  isAlreadyGrabbed?: string;
  message?: string;
}

export const claimAgentShift = async (
  data: ClaimAgentShiftPayload
): Promise<AgentShiftResponse> => {
  return await request
    .post(`${environmentConfig.REACT_APP_BASE_API_URL}/instantBookShifts/claim`)
    .set(await getAuthHeader())
    .send(data)
    .then(({ body }) => body);
};

export const fetchInstantBookShifts = async (date: string) => {
  return await request
    .get(`${environmentConfig.REACT_APP_BASE_API_URL}/instantBookShifts/${date}`)
    .retry(1, (err) => {
      if (!err) {
        return false;
      }
      return true;
    })
    .set(await getAuthHeader())
    .then(({ body }) => body)
    .catch(logApiFailureEvent);
};

export const fetchInstantBookStatusById = async (
  shiftId: string
): Promise<{ isShiftInstantBook: boolean }> => {
  return await request
    .get(`${environmentConfig.REACT_APP_BASE_API_URL}/instantBookShifts/status/${shiftId}`)
    .retry(1, (error) => {
      return Boolean(error);
    })
    .set(await getAuthHeader())
    .then(({ body }) => body)
    .catch(logAndThrowApiError);
};

/**
 *
 * @returns Is InstantPay allowed for current agent (user)?
 */
export const instantPayStatus = async (): Promise<
  | {
      _id: string;
      isHCPInstantPayProhibited: boolean;
      employmentStatus: string;
    }
  | {
      instantPayStatus: boolean;
    }
  | undefined
> => {
  try {
    const { body } = await request
      .get(`${environmentConfig.REACT_APP_BASE_API_URL}/agentprofile/instant-pay-status`)
      .retry(1, (err) => {
        if (!err) {
          return false;
        }
        return true;
      })
      .set(await getAuthHeader());
    return body;
  } catch (error) {
    logApiFailureEvent(error);
    return undefined;
  }
};

// Line below can be removed after TypeScript 4.1
type Awaited<T> = T extends PromiseLike<infer U> ? Awaited<U> : T;

export const isHCPInstantPayAllowed = (
  status: Awaited<ReturnType<typeof instantPayStatus>>
): boolean => {
  if (status) {
    return "instantPayStatus" in status
      ? Boolean(status.instantPayStatus)
      : !status.isHCPInstantPayProhibited;
  }
  return false;
};

export const fetchAgentProfile = async (): Promise<Agent | undefined> => {
  try {
    const getAgentProfileUrl = getWorkerServiceMappedURL(WorkerServiceEndpoints.GET_AGENT_PROFILE);
    const { body } = await request
      .get(getAgentProfileUrl)
      .retry(1, (err) => {
        if (!err) {
          return false;
        }
        return true;
      })
      .set(await getAuthHeader());
    return body;
  } catch (error) {
    logApiFailureEvent(error);
  }
};

export const addAgentActivityLog = async (data: object): Promise<void> => {
  try {
    await request
      .post(`${environmentConfig.REACT_APP_BASE_API_URL}/agentActivityLog/record`)
      .set(await getAuthHeader())
      .send(data);
  } catch (error) {
    logApiFailureEvent(error);
  }
};

/**
 * @deprecated /user/timezone is no longer used
 * Use useAgentProfile and extract the tmz property
 */
export const fetchCurrentTimezone = async (): Promise<string> => {
  return await request
    .get(`${environmentConfig.REACT_APP_BASE_API_URL}/user/timezone`)
    .retry(1, (err) => {
      if (!err) {
        return false;
      }
      return true;
    })
    .set(await getAuthHeader())
    .then(({ body }) => {
      moment.tz.setDefault(body.tmz);
      return body.tmz;
    })
    .catch(logApiFailureEvent);
};

export const fetchDetailedRatingsList = async (
  options: RequestDetailedRatings
): Promise<ResponseDetailedRatings[]> => {
  return await request
    .get(`${environmentConfig.REACT_APP_BASE_API_URL}/rating/list`)
    .retry(1, (err) => {
      if (!err) {
        return false;
      }
      return true;
    })
    .set(await getAuthHeader())
    .query(options)
    .then(({ body }) => body)
    .catch(logApiFailureEvent);
};
